/**
 * HeyNow widget UI (header, auth, margins, welcome initmsg).
 * botConfig mirrors legacy labanca.com.uy chatHeyNow integration (BQM-3267).
 */
export interface HeyNowWelcomeConfig {
  title: string;
  subtitle: string;
  imageUrl: string;
}

export const HEYNOW_WIDGET_DEFAULTS = {
  header: 'Chat',
  /** Empty = anonymous per vendor docs */
  auth: [] as readonly string[],
  /** Vendor parameter name is `marging` */
  marging: { l: 10, t: 10, r: 10, b: 24 },
  welcome: {
    title: 'Hola!',
    subtitle: '¿Cómo te podemos ayudar hoy?',
    imageUrl: '',
  } satisfies HeyNowWelcomeConfig,
} as const;

const HEY_NOW_FALLBACK_IMAGE = '/assets/images/labanca-logo_eticket.gif';
/** HTTPS origin for welcome image when app runs on HTTP (HeyNow iframe blocks mixed content). */
const HEY_NOW_HTTPS_ASSET_ORIGIN = 'https://bqm-frontend.alabamasolutions.com';

/** Resolves welcome image URL; HeyNow iframe is HTTPS and cannot load HTTP assets. */
export function resolveHeyNowWelcomeImageUrl(urlRoot: string, imageUrl: string): string {
  if (imageUrl) {
    return imageUrl;
  }
  if (urlRoot.startsWith('https://')) {
    return `${urlRoot}${HEY_NOW_FALLBACK_IMAGE}`;
  }
  return `${HEY_NOW_HTTPS_ASSET_ORIGIN}${HEY_NOW_FALLBACK_IMAGE}`;
}

/** Builds botConfig.initmsg when any welcome field is set (legacy parity). */
export function buildHeyNowBotConfig(
  urlRoot: string,
  welcome: HeyNowWelcomeConfig
): Record<string, unknown> | undefined {
  const { title, subtitle, imageUrl } = welcome;
  if (!title && !subtitle && !imageUrl) {
    return undefined;
  }

  return {
    initmsg: {
      attachment: {
        type: 'template',
        payload: {
          template_type: 'generic',
          image_aspect_ratio: 'square',
          elements: [
            {
              title: title || '',
              subtitle: subtitle || '',
              image_url: resolveHeyNowWelcomeImageUrl(urlRoot, imageUrl),
              buttons: [],
            },
          ],
        },
      },
      date: new Date(),
      messageId: '1',
      Hey: { state: { name: 'menuPrincipal', count: 1 } },
      msgType: 'message',
    },
  };
}

/** Builds `chatHeyNow(...)` options (vendor API). Exported for unit tests. */
export function buildHeyNowWidgetOptions(
  origin: string,
  platformId: string,
  welcome: HeyNowWelcomeConfig = HEYNOW_WIDGET_DEFAULTS.welcome
): Record<string, unknown> {
  const urlRoot = origin.replace(/\/$/, '');
  const botConfig = buildHeyNowBotConfig(urlRoot, welcome);

  return {
    platformId,
    header: HEYNOW_WIDGET_DEFAULTS.header,
    auth: [...HEYNOW_WIDGET_DEFAULTS.auth],
    wrapperCSSAbsoluteUri: `${urlRoot}/assets/heynow/heynow-chat-wrapper.css`,
    contentCSSAbsoluteUri: `${urlRoot}/assets/heynow/heynow-chat-content.css`,
    marging: { ...HEYNOW_WIDGET_DEFAULTS.marging },
    ...(botConfig === undefined ? {} : { botConfig }),
  };
}
